import React, { useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import PrimaryButton from "../ui/PrimaryButton";
import { ActionButton } from "../ui/ActionButton";
import { Toast } from '../ui/Toast';

const useTreeGeneration = () => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Mock projectId for demo
  const projectId = '3';

  const submitMeshingMethod = useCallback(async (method) => {
    if (!method) throw new Error('Please select a tree generation method');

    setIsLoading(true);
    
    try {
      // Simulate API call
      console.log('Submitting meshing method:', {
        projectId,
        method: method.toLowerCase(),
        timestamp: new Date().toISOString()
      });
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      return { success: true, method };
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  return {
    selectedMethod,
    setSelectedMethod,
    isLoading,
    submitMeshingMethod,
    projectId
  };
};

const useToast = () => {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    if (type !== 'loading') {
      setTimeout(() => setToast(null), 3000);
    }
  }, []);

  const hideToast = useCallback(() => setToast(null), []);

  return { toast, showToast, hideToast };
};

// UI Components
const Header = ({ projectId }) => (
  <header className="bg-white shadow-sm">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        <div className="text-2xl font-bold text-blue-600">
          LEAFTECH
        </div>
        <div className="text-xl font-semibold text-gray-900">
          {projectId ? `Sina_test_${projectId}` : 'Sina_test_3'}
        </div>
        <div className="text-sm text-gray-600">
          Log out
        </div>
      </div>
    </div>
  </header>
);

const MethodDescription = ({ method, title, description, isSelected }) => (
  <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
    isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
  }`}>
    <div className="flex items-start">
      <div className={`font-semibold mr-2 ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
        {method}
      </div>
      <div className={`text-sm ${isSelected ? 'text-blue-600' : 'text-gray-600'}`}>
        {description}
      </div>
    </div>
  </div>
);

const CustomSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 text-left bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`} />
        </div>
      </button>

      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className="w-full px-4 py-3 text-left hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg"
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Main Component
const TreeGenerationPage = () => {
  const { toast, showToast, hideToast } = useToast();
  const { selectedMethod, setSelectedMethod, isLoading, submitMeshingMethod, projectId } = useTreeGeneration();

  const methodOptions = [
    { value: 'manual', label: 'Manual Generation' },
    { value: 'automatic', label: 'Automatic Generation' }
  ];

  const handleBackToMenu = useCallback(() => {
    console.log('Navigating back to menu');
  }, []);

  const handleNext = useCallback(async () => {
    if (!selectedMethod) {
      showToast('Please select a tree generation method', 'error');
      return;
    }

    showToast('Setting up tree generation method...', 'loading');

    try {
      await submitMeshingMethod(selectedMethod);
      showToast('Tree generation method set successfully!', 'success');
      
      // Navigate based on selected method
      console.log(`Navigation: ${selectedMethod === 'manual' ? 'Manual' : 'Automatic'} tree setup for project ${projectId}`);
    } catch (error) {
      console.error('Error setting tree generation method:', error);
      showToast(error.message || 'Failed to set tree generation method. Please try again.', 'error');
    }
  }, [selectedMethod, submitMeshingMethod, projectId, showToast]);

  const getMethodDescription = (method) => {
    switch (method) {
      case 'manual':
        return 'create trees of a certain width and height that have not already been selected on the KML';
      case 'automatic':
        return 'generate trees automatically from the vegetation selected on the KML';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <Header projectId={projectId} />
      
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl">
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Tree generation
          </h1>

          {/* Back to Menu Button */}
          <div className="flex justify-center mb-8">
            <ActionButton 
              onClick={handleBackToMenu}
              variant="primary"
              className="px-8 py-3 uppercase tracking-wide font-semibold"
            >
              BACK TO MENU
            </ActionButton>
          </div>

          {/* Description */}
          <div className="mb-8">
            <p className="text-center text-gray-600 mb-6 leading-relaxed">
              There are two methods to generate your trees:
            </p>

            {/* Method Descriptions */}
            <div className="space-y-4 mb-8">
              <MethodDescription
                method="Manual"
                description="- create trees of a certain width and height that have not already been selected on the KML"
                isSelected={selectedMethod === 'manual'}
              />
              <MethodDescription
                method="Automatic"
                description="- generate trees automatically from the vegetation selected on the KML"
                isSelected={selectedMethod === 'automatic'}
              />
            </div>

            <p className="text-center text-gray-600 mb-6">
              Please indicate whether you want your tree to be manually or automatically generated:
            </p>
          </div>

          {/* Method Selection */}
          <div className="mb-8">
            <CustomSelect
              value={selectedMethod}
              onChange={setSelectedMethod}
              options={methodOptions}
              placeholder="– Tree Generation Method –"
              disabled={isLoading}
            />
            
            {/* Show description for selected method */}
            {selectedMethod && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{selectedMethod === 'manual' ? 'Manual' : 'Automatic'} Method:</strong>{' '}
                  {getMethodDescription(selectedMethod)}
                </p>
              </div>
            )}
          </div>

          {/* Next Button */}
          <div className="mt-8">
            <PrimaryButton 
              onClick={handleNext}
              isLoading={isLoading}
              disabled={!selectedMethod || isLoading}
              loadingText="Setting up method..."
            >
              {selectedMethod ? 'Next' : 'Please select a method'}
            </PrimaryButton>
          </div>

          {/* Helper Text */}
          {!selectedMethod && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Choose a tree generation method to continue
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreeGenerationPage;